import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress, Grid, TextField, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { compact, get, indexOf, isEmpty, isEqual, join, map, reduce, size } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { getChannelMarkers, getChannelMarkersQueryKey } from 'api/markerTypes';
import { runMultiplexBlobSegmentation } from 'api/platform';
import { getNormalizationSummary } from 'api/slideMultiplexChannelNormalizations';
import { useFilteredCaseSlideThumbnailsData } from 'components/Procedure/useFilteredCaseIds';
import { BlobSegmentationJob, JobType } from 'interfaces/job';
import { CaseSlideThumbnailData } from 'interfaces/procedure';
import { useCasesParams } from 'utils/useCasesParams';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { ActionStepper } from '../../ActionStepper';
import { ChannelsController } from '../../ChannelsController';
import { LoadFromPreviousJobStep } from '../../LoadFromPreviousJobStep';
import { usePreviousJobsAvailability } from '../../LoadFromPreviousJobStep/usePreviousJobsAvailability';
import { JobNameAndDescriptionStep } from '../../utils';
import { buildCohortWideChannelSchema, getChannelMarkersInAllSlides } from '../channelSelection';
import { buildNormalizationCoverageStatuses, resolveNormalizationSelectedSlides } from '../helpers';
import { MultiplexSelectionAlert } from '../MultiplexSelectionAlert';
import { NormalizationCheckStatus } from '../NormalizationCheckStatus';
import { useMultiplexActionSelection } from '../useMultiplexActionSelection';
import { buildBlobSegmentationNormalizationWarning, synchronizeBlobSegmentationProfiles } from './helpers';
import {
  MultiplexBlobSegmentationFormValues,
  RunMultiplexBlobSegmentationStep,
  multiplexBlobSegmentationDefaultClosingKernelSizeUm,
  multiplexBlobSegmentationDefaultMedianFilterSizeUm,
  multiplexBlobSegmentationDefaultMinBlobAreaUm2,
  multiplexBlobSegmentationDefaultOpeningKernelSizeUm,
  multiplexBlobSegmentationDefaultSmoothingKernelSizeUm,
  multiplexBlobSegmentationDefaultThreshold,
  runMultiplexBlobSegmentationStepsOrder,
} from './types';

const SNACK_BAR_KEY_RUN_MULTIPLEX_BLOB_SEGMENTATION = 'RUN_MULTIPLEX_BLOB_SEGMENTATION';

const defaultValues: MultiplexBlobSegmentationFormValues = {
  jobName: '',
  jobDescription: '',
  configParams: {
    channelNames: [],
    profiles: {},
    resolutionUmpp: null,
    normalizationName: null,
  },
};

export interface RunMultiplexBlobSegmentationProps {
  onClose: (event?: any, force?: boolean) => void;
}

export const RunMultiplexBlobSegmentation = ({ onClose }: RunMultiplexBlobSegmentationProps): ReactElement => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();
  const [activeStepIndex, setActiveStep] = useState(0);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [isLoadingSelectedPreviousJob, setIsLoadingSelectedPreviousJob] = useState(false);
  const activeStep = runMultiplexBlobSegmentationStepsOrder[activeStepIndex];
  const { casesParams } = useCasesParams();
  const {
    effectiveCasesParams,
    infoMessage: multiplexSelectionInfoMessage,
    blockingMessage: multiplexSelectionBlockingMessage,
    isLoadingSelection,
  } = useMultiplexActionSelection(casesParams);
  const multiplexCasesParams = effectiveCasesParams || casesParams;

  const { data: channelMarkersResponse } = useQuery({
    queryKey: getChannelMarkersQueryKey(multiplexCasesParams),
    queryFn: ({ signal }) => getChannelMarkers(multiplexCasesParams, signal),
    enabled: Boolean(effectiveCasesParams),
  });
  const { data: normalizationSummaries } = useQuery({
    queryKey: ['blobSegmentationNormalizationSummary', multiplexCasesParams],
    queryFn: ({ signal }) => getNormalizationSummary(multiplexCasesParams, signal),
    enabled: Boolean(effectiveCasesParams),
  });
  const { data: slideThumbnailsData } = useFilteredCaseSlideThumbnailsData({
    casesParams: multiplexCasesParams,
    enabled: Boolean(effectiveCasesParams),
  });

  const channelMarkersInAllSlides = useMemo(
    () => getChannelMarkersInAllSlides(channelMarkersResponse),
    [channelMarkersResponse]
  );
  const channelSelectSchema = useMemo(
    () =>
      buildCohortWideChannelSchema(channelMarkersInAllSlides, {
        inputLabel: 'Marker',
        getDisplayName: stainTypeIdToDisplayName,
      }),
    [channelMarkersInAllSlides, stainTypeIdToDisplayName]
  );

  const validationSchema = useMemo(
    () => ({
      [RunMultiplexBlobSegmentationStep.LoadParams]: yup.object({}),
      [RunMultiplexBlobSegmentationStep.NameAndDescription]: yup.object({
        jobName: yup.string(),
        jobDescription: yup.string(),
      }),
      [RunMultiplexBlobSegmentationStep.Configuration]: yup.object({
        configParams: yup.object({
          channelNames: channelSelectSchema.min(1, 'At least one marker is required'),
        }),
      }),
      [RunMultiplexBlobSegmentationStep.Advanced]: yup.object({
        configParams: yup.object({
          normalizationName: yup
            .string()
            .transform((value) => (value?.trim() ? value.trim() : null))
            .nullable(),
          resolutionUmpp: yup
            .number()
            .transform((value, originalValue) =>
              originalValue === '' || originalValue === null || originalValue === undefined ? null : value
            )
            .nullable()
            .notRequired()
            .typeError('Custom resolution must be a number')
            .moreThan(0, 'Custom resolution must be greater than 0'),
        }),
      }),
    }),
    [channelSelectSchema]
  );

  const methods = useForm<MultiplexBlobSegmentationFormValues>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema[activeStep]),
    defaultValues,
  });
  useTrackActionFormDirty(methods.formState.isDirty);

  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    reset,
    setValue,
    formState: { errors },
  } = methods;

  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>();

  const selectedChannelNames = watch('configParams.channelNames');
  const selectedProfiles = watch('configParams.profiles');
  const renderedProfiles = useMemo(
    () =>
      synchronizeBlobSegmentationProfiles({
        selectedChannelNames,
        profiles: selectedProfiles,
      }),
    [selectedChannelNames, selectedProfiles]
  );
  const customResolutionUmpp = watch('configParams.resolutionUmpp');
  const normalizationName = watch('configParams.normalizationName');
  const channelLabelsByName = useMemo(
    () =>
      reduce(
        selectedChannelNames,
        (accumulator, channelName) => {
          accumulator[channelName] = stainTypeIdToDisplayName(channelName);
          return accumulator;
        },
        {} as Record<string, string>
      ),
    [selectedChannelNames, stainTypeIdToDisplayName]
  );
  const { selectedSlideIds, selectedSlideThumbnails } = useMemo(
    () =>
      resolveNormalizationSelectedSlides({
        casesParams: multiplexCasesParams,
        slideThumbnailsData,
      }),
    [multiplexCasesParams, slideThumbnailsData]
  );
  const slideThumbnailDataBySlideId = useMemo(
    () =>
      reduce(
        selectedSlideThumbnails || [],
        (accumulator, slideThumbnailData) => {
          accumulator[slideThumbnailData.slideId] = slideThumbnailData;
          return accumulator;
        },
        {} as Record<string, CaseSlideThumbnailData>
      ),
    [selectedSlideThumbnails]
  );
  const hasExplicitSelectedSlideIds = size(multiplexCasesParams.slideIdsToInclude) > 0;
  const canEvaluateNormalizationCoverage =
    normalizationSummaries !== undefined && (hasExplicitSelectedSlideIds || slideThumbnailsData !== undefined);
  const normalizationCoverageStatuses = useMemo(() => {
    if (!canEvaluateNormalizationCoverage) {
      return [];
    }

    return buildNormalizationCoverageStatuses({
      normalizationSummaries,
      selectedChannelNames,
      channelLabelsByName,
      selectedSlideIds,
    });
  }, [
    canEvaluateNormalizationCoverage,
    channelLabelsByName,
    normalizationSummaries,
    selectedChannelNames,
    selectedSlideIds,
  ]);
  const normalizationCoverageByChannel = useMemo(
    () =>
      reduce(
        normalizationCoverageStatuses,
        (accumulator, coverageStatus) => {
          accumulator[coverageStatus.channelName] = coverageStatus;
          return accumulator;
        },
        {} as Record<string, (typeof normalizationCoverageStatuses)[number]>
      ),
    [normalizationCoverageStatuses]
  );
  const isNormalizationCoverageLoading =
    normalizationSummaries === undefined || (!hasExplicitSelectedSlideIds && slideThumbnailsData === undefined);
  const caseStudyId =
    typeof multiplexCasesParams.filters?.studyId === 'string' ? multiplexCasesParams.filters.studyId : undefined;
  const isAdvancedSkipped = !normalizationName && customResolutionUmpp == null;
  const advancedStepSubLabel = useMemo(() => {
    if (isAdvancedSkipped) {
      return 'Using defaults';
    }

    return join(
      compact([
        normalizationName ? 'Named normalization' : null,
        customResolutionUmpp != null ? `${customResolutionUmpp} UMPP` : null,
      ]),
      ' • '
    );
  }, [customResolutionUmpp, isAdvancedSkipped, normalizationName]);
  const normalizationWarning = useMemo(() => {
    if (!canEvaluateNormalizationCoverage) {
      return '';
    }

    return buildBlobSegmentationNormalizationWarning({
      normalizationSummaries,
      selectedChannelNames,
      channelLabelsByName,
      selectedSlideIds,
      normalizationName,
    });
  }, [
    canEvaluateNormalizationCoverage,
    channelLabelsByName,
    normalizationName,
    normalizationSummaries,
    selectedChannelNames,
    selectedSlideIds,
  ]);
  const stepWarnings = useMemo(
    () =>
      normalizationWarning
        ? {
            [indexOf(runMultiplexBlobSegmentationStepsOrder, RunMultiplexBlobSegmentationStep.Configuration)]:
              normalizationWarning,
          }
        : undefined,
    [normalizationWarning]
  );

  useEffect(() => {
    const synchronizedProfiles = synchronizeBlobSegmentationProfiles({
      selectedChannelNames,
      profiles: selectedProfiles,
    });

    if (!isEqual(synchronizedProfiles, selectedProfiles ?? {})) {
      setValue('configParams.profiles', synchronizedProfiles, {
        shouldDirty: size(selectedChannelNames) > 0,
        shouldValidate: false,
      });
    }
  }, [selectedChannelNames, selectedProfiles, setValue]);

  useEffect(() => {
    if (isAdvancedSkipped) {
      setIsStepFailed((previousState) => ({
        ...previousState,
        [indexOf(runMultiplexBlobSegmentationStepsOrder, RunMultiplexBlobSegmentationStep.Advanced)]: false,
      }));
    }
  }, [isAdvancedSkipped]);

  const runMultiplexBlobSegmentationMutation = useMutation(runMultiplexBlobSegmentation, {
    onError: () => {
      enqueueSnackbar('Error occurred, multiplex blob segmentation failed', {
        variant: 'error',
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Multiplex blob segmentation started', { variant: 'success' });
      onClose(undefined, true);
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_RUN_MULTIPLEX_BLOB_SEGMENTATION);
    },
  });

  const onSubmit: SubmitHandler<MultiplexBlobSegmentationFormValues> = async (data) => {
    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for multiplex blob segmentation to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_MULTIPLEX_BLOB_SEGMENTATION,
      autoHideDuration: null,
    });

    await runMultiplexBlobSegmentationMutation.mutateAsync({
      ...multiplexCasesParams,
      ...data,
      configParams: {
        channelNames: data.configParams.channelNames,
        profiles: synchronizeBlobSegmentationProfiles({
          selectedChannelNames: data.configParams.channelNames,
          profiles: data.configParams.profiles,
        }),
        ...(data.configParams.normalizationName ? { normalizationName: data.configParams.normalizationName } : {}),
        ...(data.configParams.resolutionUmpp != null ? { resolutionUmpp: data.configParams.resolutionUmpp } : {}),
      },
    });
  };

  const checkValidationAndSetIsStepFailed = async (step: RunMultiplexBlobSegmentationStep) => {
    if (step === RunMultiplexBlobSegmentationStep.Advanced && isAdvancedSkipped) {
      setIsStepFailed((previousState) => ({
        ...previousState,
        [indexOf(runMultiplexBlobSegmentationStepsOrder, step)]: false,
      }));
      return true;
    }

    const isValid = await trigger();
    setIsStepFailed((prev) => ({
      ...prev,
      [indexOf(runMultiplexBlobSegmentationStepsOrder, step)]: !isValid,
    }));
    return isValid;
  };

  const onSelectedJobParamChange = (newValue?: Partial<BlobSegmentationJob>) => {
    if (isEmpty(newValue)) {
      setSelectedJobId('');
      reset(defaultValues);
      return;
    }

    setSelectedJobId(newValue.id || '');
    reset({
      jobName: newValue.name || '',
      jobDescription: newValue.description || '',
      configParams: {
        ...defaultValues.configParams,
        ...(newValue.params || {}),
      },
    });
  };

  const registerDefaultedConfigurationNumberField = React.useCallback(
    (
      fieldName: any,
      {
        defaultValue,
        requiredMessage,
        minValue,
        minMessage,
      }: {
        defaultValue: number;
        requiredMessage: string;
        minValue: number;
        minMessage: string;
      }
    ) =>
      register(fieldName, {
        setValueAs: (value) => (value === '' ? defaultValue : Number(value)),
        required: requiredMessage,
        min: {
          value: minValue,
          message: minMessage,
        },
        onChange: (event) => {
          if (event.target.value === '') {
            setValue(fieldName, defaultValue, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }

          void checkValidationAndSetIsStepFailed(RunMultiplexBlobSegmentationStep.Configuration);
        },
      }),
    [register, setValue]
  );

  const { hasPreviousJobs, isCheckingPreviousJobs } = usePreviousJobsAvailability(JobType.MultiplexBlobSegmentation);

  const steps = {
    [RunMultiplexBlobSegmentationStep.LoadParams]: {
      label: 'Reuse previous job settings',
      optional: true,
      hidden: !isCheckingPreviousJobs && !hasPreviousJobs,
      isLoading: isLoadingSelectedPreviousJob,
      content: (
        <LoadFromPreviousJobStep
          jobType={JobType.MultiplexBlobSegmentation}
          onSelectedJob={(job) => onSelectedJobParamChange(job as BlobSegmentationJob)}
          onSelectedJobLoadingChange={setIsLoadingSelectedPreviousJob}
          selectedJobId={selectedJobId}
        />
      ),
    },
    [RunMultiplexBlobSegmentationStep.NameAndDescription]: {
      label: RunMultiplexBlobSegmentationStep.NameAndDescription,
      subLabel:
        activeStep !== RunMultiplexBlobSegmentationStep.NameAndDescription ? watch('jobName') || undefined : undefined,
      optional: true,
      content: <JobNameAndDescriptionStep control={control} errors={errors} register={register} />,
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(RunMultiplexBlobSegmentationStep.NameAndDescription),
    },
    [RunMultiplexBlobSegmentationStep.Configuration]: {
      label: RunMultiplexBlobSegmentationStep.Configuration,
      content: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="body2" color="text.secondary">
              Choose the markers to segment. Each selected marker uses its own blob-segmentation settings, starts with
              recommended defaults, and runs at the slide&apos;s native resolution.
            </Typography>
          </Grid>
          <Grid item>
            <ChannelsController
              name="configParams.channelNames"
              error={Boolean(get(errors, 'configParams.channelNames'))}
              helperText={get(errors, 'configParams.channelNames')?.message}
              methods={methods}
              selectAllOnLoad={false}
              additionalOnChange={() => {
                void checkValidationAndSetIsStepFailed(RunMultiplexBlobSegmentationStep.Configuration);
              }}
              casesParams={multiplexCasesParams}
              label="Markers to segment"
              placeholder="Select markers to segment"
            />
          </Grid>
          {size(selectedChannelNames) === 0 ? (
            <Grid item>
              <Typography variant="body2" color="text.secondary">
                Select one or more markers above to load the recommended segmentation settings.
              </Typography>
            </Grid>
          ) : null}
          <Grid item container spacing={2}>
            {map(selectedChannelNames, (channelName) => {
              const channelDisplayName = channelLabelsByName[channelName] || channelName;
              const channelProfile = renderedProfiles[channelName];
              const medianFilterFieldName = `configParams.profiles.${channelName}.medianFilterSizeUm` as any;
              const thresholdFieldName = `configParams.profiles.${channelName}.threshold` as any;
              const openingKernelFieldName = `configParams.profiles.${channelName}.openingKernelSizeUm` as any;
              const closingKernelFieldName = `configParams.profiles.${channelName}.closingKernelSizeUm` as any;
              const minBlobAreaFieldName = `configParams.profiles.${channelName}.minBlobAreaUm2` as any;
              const smoothingKernelFieldName = `configParams.profiles.${channelName}.smoothingKernelSizeUm` as any;

              return (
                <React.Fragment key={channelName}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">{channelDisplayName}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <NormalizationCheckStatus
                      coverageStatus={normalizationCoverageByChannel[channelName]}
                      isLoading={isNormalizationCoverageLoading}
                      caseStudyId={caseStudyId}
                      slideThumbnailDataBySlideId={slideThumbnailDataBySlideId}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Noise cleanup radius (um)"
                      type="number"
                      defaultValue={channelProfile?.medianFilterSizeUm}
                      inputProps={{ min: 0, step: 0.1 }}
                      error={Boolean(get(errors, `configParams.profiles.${channelName}.medianFilterSizeUm`))}
                      helperText={
                        get(errors, `configParams.profiles.${channelName}.medianFilterSizeUm`)?.message ||
                        `Default: ${multiplexBlobSegmentationDefaultMedianFilterSizeUm}. Smooths speckled noise before segmentation. Set 0 to skip.`
                      }
                      {...registerDefaultedConfigurationNumberField(medianFilterFieldName, {
                        defaultValue: multiplexBlobSegmentationDefaultMedianFilterSizeUm,
                        requiredMessage: 'Median filter size is required',
                        minValue: 0,
                        minMessage: 'Median filter size must be at least 0',
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Signal threshold"
                      type="number"
                      defaultValue={channelProfile?.threshold}
                      inputProps={{ min: 0, step: 0.01 }}
                      error={Boolean(get(errors, `configParams.profiles.${channelName}.threshold`))}
                      helperText={
                        get(errors, `configParams.profiles.${channelName}.threshold`)?.message ||
                        `Default: ${multiplexBlobSegmentationDefaultThreshold}. Minimum signal intensity required before a region is kept for this marker.`
                      }
                      {...registerDefaultedConfigurationNumberField(thresholdFieldName, {
                        defaultValue: multiplexBlobSegmentationDefaultThreshold,
                        requiredMessage: 'Threshold is required',
                        minValue: 0,
                        minMessage: 'Threshold must be at least 0',
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Small artifact removal (um)"
                      type="number"
                      defaultValue={channelProfile?.openingKernelSizeUm}
                      inputProps={{ min: 0, step: 0.1 }}
                      error={Boolean(get(errors, `configParams.profiles.${channelName}.openingKernelSizeUm`))}
                      helperText={
                        get(errors, `configParams.profiles.${channelName}.openingKernelSizeUm`)?.message ||
                        `Default: ${multiplexBlobSegmentationDefaultOpeningKernelSizeUm}. Removes tiny bright specks before final regions are created. Set 0 to skip.`
                      }
                      {...registerDefaultedConfigurationNumberField(openingKernelFieldName, {
                        defaultValue: multiplexBlobSegmentationDefaultOpeningKernelSizeUm,
                        requiredMessage: 'Opening kernel size is required',
                        minValue: 0,
                        minMessage: 'Opening kernel size must be at least 0',
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Gap bridging (um)"
                      type="number"
                      defaultValue={channelProfile?.closingKernelSizeUm}
                      inputProps={{ min: 0, step: 0.1 }}
                      error={Boolean(get(errors, `configParams.profiles.${channelName}.closingKernelSizeUm`))}
                      helperText={
                        get(errors, `configParams.profiles.${channelName}.closingKernelSizeUm`)?.message ||
                        `Default: ${multiplexBlobSegmentationDefaultClosingKernelSizeUm}. Connects nearby signal and fills small holes inside a region. Set 0 to skip.`
                      }
                      {...registerDefaultedConfigurationNumberField(closingKernelFieldName, {
                        defaultValue: multiplexBlobSegmentationDefaultClosingKernelSizeUm,
                        requiredMessage: 'Closing kernel size is required',
                        minValue: 0,
                        minMessage: 'Closing kernel size must be at least 0',
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Minimum region area (um^2)"
                      type="number"
                      defaultValue={channelProfile?.minBlobAreaUm2}
                      inputProps={{ min: 0, step: 1 }}
                      error={Boolean(get(errors, `configParams.profiles.${channelName}.minBlobAreaUm2`))}
                      helperText={
                        get(errors, `configParams.profiles.${channelName}.minBlobAreaUm2`)?.message ||
                        `Default: ${multiplexBlobSegmentationDefaultMinBlobAreaUm2}. Discards segmented regions smaller than this area.`
                      }
                      {...registerDefaultedConfigurationNumberField(minBlobAreaFieldName, {
                        defaultValue: multiplexBlobSegmentationDefaultMinBlobAreaUm2,
                        requiredMessage: 'Minimum blob area is required',
                        minValue: 0,
                        minMessage: 'Minimum blob area must be at least 0',
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Boundary smoothing (um)"
                      type="number"
                      defaultValue={channelProfile?.smoothingKernelSizeUm}
                      inputProps={{ min: 0, step: 0.1 }}
                      error={Boolean(get(errors, `configParams.profiles.${channelName}.smoothingKernelSizeUm`))}
                      helperText={
                        get(errors, `configParams.profiles.${channelName}.smoothingKernelSizeUm`)?.message ||
                        `Default: ${multiplexBlobSegmentationDefaultSmoothingKernelSizeUm}. Smooths jagged region edges after segmentation. Set 0 to skip.`
                      }
                      {...registerDefaultedConfigurationNumberField(smoothingKernelFieldName, {
                        defaultValue: multiplexBlobSegmentationDefaultSmoothingKernelSizeUm,
                        requiredMessage: 'Smoothing kernel size is required',
                        minValue: 0,
                        minMessage: 'Smoothing kernel size must be at least 0',
                      })}
                    />
                  </Grid>
                </React.Fragment>
              );
            })}
          </Grid>
        </Grid>
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(RunMultiplexBlobSegmentationStep.Configuration),
    },
    [RunMultiplexBlobSegmentationStep.Advanced]: {
      label: RunMultiplexBlobSegmentationStep.Advanced,
      subLabel: advancedStepSubLabel,
      optional: true,
      skip: isAdvancedSkipped,
      content: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="body2" color="text.secondary">
              Optional overrides. Leave these empty to use the latest normalization per slide and the native slide
              resolution.
            </Typography>
          </Grid>
          <Grid item container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Normalization result name"
                error={Boolean(get(errors, 'configParams.normalizationName'))}
                helperText={
                  get(errors, 'configParams.normalizationName')?.message ||
                  'Optional named normalization result. Leave empty to use the latest available result for each slide.'
                }
                {...register('configParams.normalizationName', {
                  setValueAs: (value) => (value?.trim() ? value.trim() : null),
                  onChange: () => {
                    void checkValidationAndSetIsStepFailed(RunMultiplexBlobSegmentationStep.Advanced);
                  },
                })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Custom resolution (UMPP)"
                type="number"
                inputProps={{ min: 0.000001, step: 0.01 }}
                error={Boolean(get(errors, 'configParams.resolutionUmpp'))}
                helperText={
                  get(errors, 'configParams.resolutionUmpp')?.message ||
                  'Recommended: leave empty. Native slide resolution is used automatically unless you need an expert override.'
                }
                {...register('configParams.resolutionUmpp', {
                  setValueAs: (value) => (value === '' || value === null || value === undefined ? null : Number(value)),
                  onChange: () => {
                    void checkValidationAndSetIsStepFailed(RunMultiplexBlobSegmentationStep.Advanced);
                  },
                })}
              />
            </Grid>
          </Grid>
        </Grid>
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(RunMultiplexBlobSegmentationStep.Advanced),
    },
  };

  if (isLoadingSelection) {
    return (
      <MultiplexSelectionAlert
        severity="info"
        title="Checking slide selection"
        message="Resolving the multiplex slides for the current case selection."
      />
    );
  }

  if (multiplexSelectionBlockingMessage) {
    return (
      <MultiplexSelectionAlert
        severity="error"
        title="Select slides for this multiplex action"
        message={multiplexSelectionBlockingMessage}
      />
    );
  }

  return (
    <Grid container direction="column" spacing={2}>
      {multiplexSelectionInfoMessage ? (
        <Grid item>
          <MultiplexSelectionAlert
            severity="info"
            title="This action runs on slides"
            message={multiplexSelectionInfoMessage}
          />
        </Grid>
      ) : null}
      <Grid item>
        <ActionStepper
          handleSubmit={handleSubmit(onSubmit)}
          steps={map(runMultiplexBlobSegmentationStepsOrder, (step) => steps[step])}
          setActiveStepForValidation={setActiveStep}
          isStepFailed={isStepFailed}
          isInitializing={isCheckingPreviousJobs}
          initializingText="Checking for previous jobs…"
          stepWarnings={stepWarnings}
          validateStep={() => checkValidationAndSetIsStepFailed(activeStep)}
        />
      </Grid>
    </Grid>
  );
};
